/**
 * Proposal Detail Page - Handles nested routes for proposal views
 * 
 * Routes:
 * - /rfp/proposals-management/current/:id - View proposal with default tab
 * - /rfp/proposals-management/current/:id/:subtab - View proposal with specific subtab
 * 
 * Subtabs: content, citations, staff-assignments, versions, word-count, actions, comments, sharing, analytics
 */

import React, { useEffect, useState } from 'react';
import { ProposalListPage } from './ProposalList';

export function ProposalDetailPage() {
  const [extractedProposalId, setExtractedProposalId] = useState<string | undefined>(undefined);
  const [extractedSubtab, setExtractedSubtab] = useState<string | undefined>(undefined);
  
  useEffect(() => {
    // Parse URL path: /rfp/proposals-management/current/:id/:subtab
    const path = window.location.pathname;
    const pathParts = path.split('/').filter(p => p);
    
    // Find index of 'current' in path
    const currentIndex = pathParts.indexOf('current');
    if (currentIndex >= 0 && currentIndex < pathParts.length - 1) {
      const id = pathParts[currentIndex + 1];
      setExtractedProposalId(id);
      
      // Check for subtab
      if (currentIndex + 2 < pathParts.length) {
        const subtabFromPath = pathParts[currentIndex + 2];
        setExtractedSubtab(subtabFromPath);
      }
    }
    
    // Also check query params as fallback
    const urlParams = new URLSearchParams(window.location.search);
    if (!extractedProposalId && urlParams.get('proposal_id')) {
      setExtractedProposalId(urlParams.get('proposal_id') || undefined);
    }
    if (!extractedSubtab && urlParams.get('subtab')) {
      setExtractedSubtab(urlParams.get('subtab') || undefined);
    }
  }, []);
  
  // Map subtab names to ReportViewer tab IDs
  const subtabMap: Record<string, string> = {
    'citations': 'citations',
    'staff-assignments': 'staffassignments',
    'versions': 'versions',
    'word-count': 'wordcount',
    'actions': 'actions',
    'comments': 'collaboration',
    'sharing': 'sharing',
    'analytics': 'analytics',
    'content': 'content',
  };
  
  const reportViewerTab = extractedSubtab ? subtabMap[extractedSubtab] || 'content' : 'content';
  
  // Update URL to include proposal_id and subtab as query params for ProposalListPage
  useEffect(() => {
    if (extractedProposalId) {
      const url = new URL(window.location.href);
      url.searchParams.set('proposal_id', extractedProposalId);
      url.searchParams.set('tab', 'list');
      if (reportViewerTab && reportViewerTab !== 'content') {
        url.searchParams.set('report_tab', reportViewerTab);
      }
      window.history.replaceState({}, '', url.toString());
    }
  }, [extractedProposalId, reportViewerTab]);
  
  return <ProposalListPage />;
}
